// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

export const groupMap = <T, TKey, TElement, TResult>(
  keySelector: (x: T) => TKey,
  elementSelector: (x: T) => TElement,
  resultSelector: (x: TKey, xs: TElement[]) => TResult,
  source: T[],
) =>
  Array.from(
    source
      .reduce((map, value) => {
        const key = keySelector(value);

        if (!map.has(key)) {
          map.set(key, []);
        }

        map.get(key)!.push(elementSelector(value));

        return map;
      }, new Map<TKey, TElement[]>())
      .entries(),
  ).map(([key, values]) => resultSelector(key, values)) as TResult extends any[]
    ? TResult
    : TResult[];

export const groupBy = <T, TKey extends keyof T>(
  data: T[],
  key: TKey,
): Map<T[TKey], T[]> => {
  const result = data.reduce((group, value) => {
    const keyValue = value[key];

    if (!group.has(keyValue)) {
      group.set(keyValue, []);
    }

    group.get(keyValue)!.push(value);

    return group;
  }, new Map<T[TKey], T[]>());

  return result;
};
